import React from 'react';
import { HeartIcon, ArrowRightIcon, SparklesIcon, DollarSignIcon, CheckCircleIcon } from 'lucide-react';
export function Hero() {
  return <section className="relative bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 py-8 md:py-16 overflow-hidden" data-id="element-386">
      {/* Dynamic animated background with liquid glass effect */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-id="element-387">
        {/* Animated gradient blobs */}
        <div className="absolute top-0 left-0 w-80 h-80 bg-gradient-to-br from-blue-400/30 to-cyan-400/30 rounded-full blur-3xl animate-float" data-id="element-388"></div>
        <div className="absolute top-1/4 right-0 w-[380px] h-[380px] bg-gradient-to-br from-cyan-300/25 to-blue-300/25 rounded-full blur-3xl animate-float-reverse" data-id="element-389"></div>
        <div className="absolute bottom-0 left-1/3 w-[360px] h-[360px] bg-gradient-to-br from-blue-300/20 to-cyan-300/20 rounded-full blur-3xl animate-float-slow" data-id="element-390"></div>
        <div className="absolute top-1/2 left-1/4 w-52 h-52 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 rounded-full blur-2xl animate-float" data-id="element-391"></div>
        {/* Glass overlay effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5" data-id="element-392"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-393">
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-10" data-id="element-394">
          <div className="w-full md:w-1/2 mb-4 md:mb-0" data-id="element-395">
            {/* Trust badge with glass effect */}
            <div className="inline-flex items-center gap-2 glass-effect rounded-full px-4 py-1.5 mb-3 shadow-lg" data-id="element-396">
              <SparklesIcon className="text-blue-600" size={16} aria-hidden="true" data-id="element-397" />
              <span className="text-slate-900 text-xs md:text-sm font-semibold tracking-wide uppercase" data-id="element-398">
                4+ Years of Excellence
              </span>
            </div>

            <h1 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight mb-3 tracking-tight" data-id="element-399">
              IVF in Europe,{' '}
              <span className="relative inline-block pb-3 md:pb-4" data-id="element-400">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 px-1 py-1" data-id="element-401">
                  60–80% Less
                </span>
                <svg className="absolute -bottom-1 left-0 w-full h-auto" viewBox="0 0 200 10" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" aria-hidden="true" data-id="element-402">
                  <path d="M2 6C50 2 150 2 198 6" stroke="url(#paint0_linear_blue)" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke" data-id="element-403" />
                  <defs data-id="element-404">
                    <linearGradient id="paint0_linear_blue" x1="0" y1="0" x2="200" y2="0" data-id="element-405">
                      <stop stopColor="#2563eb" data-id="element-406" />
                      <stop offset="0.5" stopColor="#06b6d4" data-id="element-407" />
                      <stop offset="1" stopColor="#2563eb" data-id="element-408" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>{' '}
              Than at Home
            </h1>

            <p className="text-base md:text-lg text-slate-700 mb-3 font-medium leading-relaxed" data-id="element-409">
              Treatment quality meets or exceeds U.S. standards. Nura handles
              everything else.
            </p>
            <p className="text-sm md:text-base text-slate-600 mb-4 leading-relaxed" data-id="element-410">
              Premier EU fertility clinics with full coordination from first
              consultation to post-treatment care. We manage logistics,
              paperwork, and travel so you can focus on what matters most.
            </p>

            {/* Value props bullets */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 mb-5 border border-slate-200 shadow-lg" data-id="element-411">
              <div className="space-y-2.5" data-id="element-412">
                <div className="flex items-center gap-3" data-id="element-413">
                  <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-2 rounded-lg flex-shrink-0" data-id="element-414">
                    <DollarSignIcon className="text-white" size={18} data-id="element-415" />
                  </div>
                  <p className="text-slate-900 font-semibold text-xs md:text-sm" data-id="element-416">
                    60-80% lower costs vs U.S., UK, Canada
                  </p>
                </div>
                <div className="flex items-center gap-3" data-id="element-417">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2 rounded-lg flex-shrink-0" data-id="element-418">
                    <CheckCircleIcon className="text-white" size={18} data-id="element-419" />
                  </div>
                  <p className="text-slate-900 font-semibold text-xs md:text-sm" data-id="element-420">
                    1 dedicated coordinator handling all logistics
                  </p>
                </div>
                <div className="flex items-center gap-3" data-id="element-421">
                  <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 p-2 rounded-lg flex-shrink-0" data-id="element-422">
                    <CheckCircleIcon className="text-white" size={18} data-id="element-423" />
                  </div>
                  <p className="text-slate-900 font-semibold text-xs md:text-sm" data-id="element-424">
                    3–6 days in Europe, tests done at home
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3" data-id="element-425">
              <a href="/contact" className="group bg-blue-600 text-white px-6 md:px-8 py-2.5 rounded-full text-center hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-blue-600/30 hover:scale-105 flex items-center justify-center gap-2 text-sm md:text-base font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-426">
                Free Consultation
                <ArrowRightIcon className="group-hover:translate-x-1 transition-transform" size={18} aria-hidden="true" data-id="element-427" />
              </a>
              <a href="/treatments" className="glass-effect text-slate-900 px-6 md:px-8 py-2.5 rounded-full text-center hover:bg-white/50 transition-all duration-300 text-sm md:text-base font-semibold shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-428">
                Explore Treatments
              </a>
            </div>

            {/* Stats with glass effect (hide on very small screens) */}
            <div className="grid grid-cols-3 gap-3 mt-6 hidden sm:grid" data-id="element-429">
              <div className="text-center glass-effect p-3 rounded-2xl shadow-lg hover:scale-105 transition-transform" data-id="element-430">
                <div className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-blue-600 to-cyan-600 mb-1 tracking-normal px-1 py-1 inline-block" data-id="element-431">
                  200+
                </div>
                <div className="text-slate-900 text-[11px] md:text-xs font-semibold tracking-normal" data-id="element-432">
                  Happy Families
                </div>
              </div>
              <div className="text-center glass-effect p-3 rounded-2xl shadow-lg hover:scale-105 transition-transform" data-id="element-433">
                <div className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-cyan-600 to-blue-600 mb-1 tracking-normal px-1 py-1 inline-block" data-id="element-434">
                  10+
                </div>
                <div className="text-slate-900 text-[11px] md:text-xs font-semibold tracking-normal" data-id="element-435">
                  Partner Clinics
                </div>
              </div>
              <div className="text-center glass-effect p-3 rounded-2xl shadow-lg hover:scale-105 transition-transform" data-id="element-436">
                <div className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-blue-600 to-cyan-600 mb-1 tracking-normal px-1 py-1 inline-block" data-id="element-437">
                  3
                </div>
                <div className="text-slate-900 text-[11px] md:text-xs font-semibold tracking-normal" data-id="element-438">
                  EU Countries
                </div>
              </div>
            </div>
          </div>

          {/* Image only on md+ to keep mobile height tight */}
          <div className="hidden md:block md:w-1/2" data-id="element-439">
            <div className="relative" data-id="element-440">
              {/* Glass effect behind image */}
              <div className="absolute inset-0 glass-effect rounded-3xl opacity-60" aria-hidden="true" data-id="element-441"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-3xl blur-2xl" aria-hidden="true" data-id="element-442"></div>
              <img src="https://images.unsplash.com/photo-1531983412531-1f49a365ffed?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Happy family with newborn baby smiling together" className="relative rounded-3xl shadow-2xl w-full max-w-md mx-auto hover:scale-105 transition-transform duration-500" width="800" height="600" data-id="element-443" />
              {/* Floating card with glass effect */}
              <div className="absolute -bottom-4 -right-4 glass-effect p-4 rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-300" data-id="element-444">
                <div className="flex items-center gap-3" data-id="element-445">
                  <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-3 rounded-xl shadow-lg" aria-hidden="true" data-id="element-446">
                    <DollarSignIcon className="text-white" size={20} data-id="element-447" />
                  </div>
                  <div data-id="element-448">
                    <div className="text-lg font-bold text-slate-900" data-id="element-449">
                      60-80%
                    </div>
                    <div className="text-slate-900 text-xs font-semibold" data-id="element-450">
                      Cost Savings
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}